// All site content lives here so the owner can replace text without touching components.
// Replace any field below; the site updates automatically.

export const owner = {
  name: "Rajib Sengupta",
  tagline: "From steel and systems to stories, summits, and self-discovery.",
  shortBio:
    "My journey began in Jamshedpur, the steel city of India, where I spent my formative years and completed my schooling. Growing up in a place shaped by industry, discipline, and community quietly influenced the way I would approach both life and work in the years to come.",
  location: "Pune, India",
  profilePhoto:
    "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1780943384/profile_nqepta.png",
};

export const journey = {
  earlyYears: {
    eyebrow: "1986 — 2000",
    title: "Formative Years",
    paragraphs: [
      "I started my professional career with Tata Steel, where I spent fourteen meaningful years. Those early experiences in manufacturing instilled in me a strong foundation of structure, resilience, and problem-solving. Yet, somewhere along the way, a curiosity for technology began to grow — eventually leading me to take a bold step into the world of information technology.",
    ],
  },
  career: {
    eyebrow: "2000 — Today",
    title: "Tech Journey & Now",
    paragraphs: [
      "My career in IT spans several decades, witnessing the evolution from legacy systems to modern computing. I started as an application developer, learning the craft line by line, and gradually evolved into ERP consulting. Over time, I moved into leadership roles, ultimately concluding my IT career as a Program Delivery Manager. Across more than two decades in IT, I had the privilege of working on diverse challenges, building solutions, and collaborating with remarkable people.",
      "This journey taught me adaptability, continuous learning, and problem-solving. My relationship with technology continues — in recent times, I have found myself drawn once again to learning, exploring emerging areas such as Artificial Intelligence and other evolving technologies. For me, learning has never been just a phase; it has been a constant companion.",
      "Today, life has taken on a different rhythm — one that is slower, more reflective, and deeply fulfilling. I now spend my time exploring long-held passions: reading, travelling across India, trekking in the majestic Himalayas, and immersing myself in music. Whether it's the quiet of a mountain trail or the pages of a thoughtful book, I find joy in experiences that connect me with both nature and self.",
    ],
  },
};

export const currentRead = {
  title: "Sapiens: A Brief History of Humankind",
  author: "Yuval Noah Harari",
  cover: "https://covers.openlibrary.org/b/isbn/9780062316097-L.jpg",
  progress: "Non-fiction · currently reading",
  thoughts:
    "A sweeping, provocative narrative that traces the journey of Homo sapiens from small bands of foragers on the African savannah to the species that now shapes the fate of the planet. Harari weaves biology, history, and economics into one big, opinionated story — the Cognitive, Agricultural, and Scientific revolutions — and keeps asking the uncomfortable question: are we happier for all of it?",
};

export const pastReads = [
  {
    title: "My Autobiography",
    author: "Charles Chaplin",
    cover: "https://customer-assets.emergentagent.com/job_melody-trekker/artifacts/gnxnehd7_My%20Autobiography%20by%20Charles%20Chaplin.jpg",
    summary:
      "Chaplin's own account of his extraordinary life — from a bleak Victorian childhood in the workhouses of London, through the vaudeville circuit, to becoming the most recognised face on earth in the silent-film era. He writes candidly about his rise to global stardom, his creative battles, the friendships and loves that shaped him, and the political storms that eventually drove him from America.",
    thoughts:
      "The book invites readers to reflect on how adversity can shape greatness and how creativity often emerges from hardship. It also raises deeper questions about fame, loneliness, identity, and the price one pays for artistic brilliance.",
  },
  {
    title: "গুরুদেব (Gurudev)",
    author: "রানী চন্দ (Rani Chanda)",
    cover: "https://customer-assets.emergentagent.com/job_melody-trekker/artifacts/bre761hk_Gurudeb%20By%20Rani%20Chanda.jpg",
    summary:
      "A deeply intimate portrayal of Rabindranath Tagore through the eyes of someone who observed him closely in his later years. The book captures his simplicity, humanity, and the quiet depth behind the revered figure of 'Gurudev'.",
    thoughts:
      "It encourages readers to look beyond the legend and see the person — gentle, reflective, and profoundly humane. The narrative evokes a sense of closeness with Tagore, prompting reflection on creativity, humility, and the essence of a life devoted to art and thought.",
  },
  {
    title: "মংপুতে রবীন্দ্রনাথ (Mongpu-te Rabindranath)",
    author: "মৈত্রেয়ী দেবী (Maitreyi Devi)",
    cover: "https://customer-assets.emergentagent.com/job_melody-trekker/artifacts/cmutzeg2_Mongpu%20te%20Rabindranath.jpg",
    summary:
      "A tender memoir of Rabindranath Tagore's stays in Mongpu, capturing his daily life, conversations, and quiet creative moments. Through personal recollections, Maitreyi Devi reveals a warm, intimate side of the poet far removed from his public image.",
    thoughts:
      "The book evokes a deep sense of closeness to Tagore, highlighting his simplicity, humour, and emotional depth. It invites readers to reflect on the beauty of human connection, the serenity of nature, and how creativity often blossoms in moments of stillness and companionship.",
  },
];

// ---------------------------------------------------------------------------
// TREKS — scalable structure
//
// To add a new trek later:
//   1. Upload photos to Cloudinary → copy each photo URL.
//   2. Upload videos to YouTube as "Unlisted" → copy the 11-char video ID
//      (the bit after v= in the URL, e.g. dQw4w9WgXcQ).
//   3. Add one new { ... } block below with a unique `slug`.
//
// Fields:
//   slug           URL segment (lowercase, hyphenated, no spaces)
//   name           Display title
//   region         State / area
//   month          Month & year (e.g. "November 2023")
//   elevation      Highest point (e.g. "12,083 ft (3,682 m)")
//   shortDescription   1–2 sentences for the card on the index page
//   about          Full paragraph(s) for the detail page (array of strings)
//   memory         One standout memory highlighted in its own block
//   coverImage     Hero image shown on card + detail hero
//   gallery        Array of image URLs (Cloudinary). 12–15 per trek is ideal.
//   videos         Array of YouTube video IDs (empty array if none)
// ---------------------------------------------------------------------------

export const treks = [
  {
    slug: "deoriatal-baniakund-chandrashila",
    name: "Deoriatal–Baniakund–Chandrashila",
    region: "Uttarakhand, India",
    month: "November 2023",
    elevation: "12,083 ft (3,682 m) at Chandrashila Peak",
    shortDescription:
      "A five-day crossover from the mirror-still Deoriatal lake to the 360° Himalayan summit of Chandrashila — reflections of the Chaukhamba peaks, rhododendrons, and the world's highest Shiva temple along the way.",
    about: [
      "The Deoriatal–Baniakund–Chandrashila trek is one of India's most popular all-season treks, set in the Rudraprayag district of Uttarakhand. About 28–30 km spread across 4–5 easy days, it is a wonderful introduction to the Garhwal Himalayas.",
      "Deoriatal (or Devariyatal) is a pristine high-altitude lake at 2,438 m, near Sari village, famous for crystal-clear reflections of the Chaukhamba peaks. Local lore says this is the lake where the Pandavas were questioned by the Yaksha in the Mahabharata.",
      "Chandrashila, at 3,690 m in the Chopta region, is celebrated as the 'Moon Rock' of Hindu mythology, where Lord Rama is said to have meditated. Just below the summit stands Tungnath, the highest Shiva temple in the world.",
    ],
    memory:
      "The early morning summit to Chandrashila gave us the chance to witness the sunrise with a clear, breathtaking view of the Chaukhamba peaks.",
    coverImage:
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722429/chandrashila_summit_achieved_u3bmc1.jpg",
    gallery: [
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722429/base_camp_sari_village_u0rfgg.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722430/deoria_tal_kswrnn.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722430/deoria_tal_2_tx4fve.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722433/rohini_bugyal_ikewke.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722433/tunganath_temple_deaera.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722433/first_ray_of_sun_on_chowkhamba_v0lfxn.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722430/descending_from_chandrashila_wmtsoi.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722430/chowkhamba_and_the_ridge_twfidc.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722430/chowkhamba_and_other_peaks_kmdrvq.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722429/chadrashila_wxh0gi.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722429/chandrashila_peak_sunrise_ecnbid.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722429/chandrashila_trek_certificate_distribution_wzmydl.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722430/chandrashila_trek_closing_bqlma0.jpg",
    ],
    videos: [],
  },
  {
    slug: "kedarnath",
    name: "Kedarnath",
    region: "Uttarakhand, India",
    month: "November 2023",
    elevation: "11,760 ft (3,583 m)",
    shortDescription:
      "A 16–18 km pilgrimage through the Garhwal Himalayas from Gaurikund to the 8th-century Kedarnath Temple — a solo trek that ended in a silent, snow-white valley.",
    about: [
      "The Kedarnath trek is a popular 16–18 km one-way pilgrimage route in Uttarakhand's Garhwal Himalayas, starting from Gaurikund and climbing to the 3,583 m high Kedarnath Temple.",
      "Kedarnath, an 8th-century, 1,200-year-old temple built from massive stone slabs, is one of the most revered Hindu pilgrimage sites in the country, dedicated to Lord Shiva and set against a dramatic amphitheatre of snow peaks.",
    ],
    memory:
      "I did this trek solo. By the time I reached the temple, it started snowing heavily — the entire Kedarnath valley turned white. The next morning the sky was clear, and the temple stood there with snow-capped peaks behind it, breathtakingly beautiful.",
    coverImage:
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722512/kedarnath_temple_afternoon_snowing_rtfpos.jpg",
    gallery: [
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722512/gaurimata_mandir_gaurikund_gutalh.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722515/mandakini_in_gaurikund_dkq42c.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722514/mandakini_pztn7q.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722513/kedarnath_temple_early_morning_sb6f2d.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722511/bhim_shila_smerog.jpg",
    ],
    videos: ["K8vPxvNSP5U", "IS98KHcU85I", "fg6MgkG29A0", "-OWSwIekUgs"],
  },
  {
    slug: "kashmir-pir-panjal-lakes",
    name: "Kashmir Pir-Panjal Lakes",
    region: "Kashmir, India",
    month: "August 2024",
    elevation: "11,450 ft (3,490 m)",
    shortDescription:
      "Through boulder-strewn valleys and dense forests into the Pir Panjal range — past the high-altitude lakes of Nandan Sar, Chandan Sar and Neel Sar, and the summer camps of Kashmiri shepherds.",
    about: [
      "The Pir Panjal range is a natural, snow-clad barrier separating the Jammu plains from the Kashmir Valley. Peer Ki Gali — the Pir Panjal Pass — sits at 3,490 m on the historic Mughal Road connecting Rajouri/Poonch with the Valley.",
      "The region is dotted with high-altitude lakes like Nandan Sar, Chandan Sar and Neel Sar, usually sitting between 13,000 and 14,500 ft. Together they make one of the quieter, less-walked trails in the western Himalayas.",
    ],
    memory:
      "The experience of trekking through boulder-ridden valleys and dense forests. The high-altitude lakes were beautiful, and the summer villages of the Kashmiri shepherds with their livestock were quite unlike anything I had seen before.",
    coverImage:
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725141/self_at_first_campsite_vzrjm1.jpg",
    gallery: [
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725118/chandimarh_base_camp_w4oadf.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725112/base_camp_group_pic_hpc13h.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725148/waterfall_at_chandimarh_jamjqe.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725145/stream_at_chandimarh_nf8gzs.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725139/self_at_first_camp_zhckes.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725121/first_campsite__rowrod.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725120/first_campsite_nhsbzp.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725137/scenic_valleys_x7q9s5.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725111/a_lonely_shepherd_gq3wlq.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725130/in_a_jolly_good_mood_ckm1yx.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725129/hiking_through_valley_n5sfia.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725125/getting_your_breath_back_wel99c.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725146/walking_amidst_the_clouds_dqvxvc.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725110/2nd_campsite__qtc4jd.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725117/2nd_campsite_dqirza.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725143/song___dance-2nd_campsite_kntmpm.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725134/on_way_to_3rd_campsite_sqvx83.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725132/neelsar_3rd_campsite_de0jk4.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725127/given_up_bvqb1h.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725136/on_way_to_chandansar_rqqhol.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725116/chandansar_twilight_zmk4be.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725115/chandansar_4th_campsite_mgngr7.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777725114/celebration_hsixqp.jpg",
    ],
    videos: ["L5zbolDaU60", "fyD7vMwyBY0", "PsFVl9jUM_U"],
  },
  {
    slug: "kedarkantha",
    name: "Kedarkantha",
    region: "Uttarakhand, India",
    month: "January 2026",
    elevation: "12,500 ft (3,810 m)",
    shortDescription:
      "A popular winter trek through snow-clad pine forests and the frozen Juda-ka-Talab, ending with a 360° sunrise over Swargarohini, Bandarpoonch and Black Peak from the 12,500 ft summit.",
    about: [
      "Kedarkantha is famous for its 360-degree Himalayan views, its pine forests and the frozen lake at Juda-ka-Talab. It is perhaps the most loved winter trek in the Indian Himalayas.",
      "The 12,500 ft summit offers a panoramic view of the Garhwal Himalayas, featuring Swargarohini (I–IV), Bandarpoonch (I & II) and Black Peak (Kalanag). Other visible peaks include Ranglana and Draupadi ka Danda, and on clear days even the Yamunotri and Gangotri ranges.",
    ],
    memory: "Sunrise at the Kedarkantha peak — the kind of silence one walks a long way to hear.",
    coverImage:
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722681/kedarkantha_summit_vjhgvy.jpg",
    gallery: [
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722688/sankri_base_camp_i1891w.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722676/first_campsite_ipfrgt.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722679/juda_ka_talab_sunset_potylm.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722678/frozen_juda_ka_talab_qifwvv.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722684/leaving_first_campsite_kvhp2j.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722685/on_way_to_2nd_campsite_dhofnr.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722674/2nd_campsite_gfgg11.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722696/valley___mountain_yadgya.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722693/two_freinds_tm4kzw.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722691/trekking_through_forest_oxediu.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722686/on_way_to_summit_gno3yv.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722689/summit_photo_b5m66x.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722683/kedarkantha_summit_group_photo_r9hqar.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722682/kedarkantha_summit__dtvvjg.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722697/view_of_sunrise_from_kedarkantha_peak_eubv2w.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722690/the_last_camp_hargaon_mmb38q.jpg",
      "https://res.cloudinary.com/ds1r4hnhr/image/upload/v1777722694/uttarakhand_pahadi_cap_c6nccs.jpg",
    ],
    videos: [],
  },
];

export const ownSongs = [
  {
    title: "When a Child is Born",
    youtubeId: "MsYd49w6c-o",
    note:
      "Johnny Mathis' famous Christmas standard. A song I keep coming back to — for its quiet hopefulness about the world a child is born into.",
  },
  {
    title: "Na Tum Hamein Jaano",
    youtubeId: "7jB45-JUOAk",
    note:
      "A Hemant Kumar classic from Baat Ek Raat Ki (1962). I learned this one on the guitar over many slow Sundays.",
  },
  {
    title: "Jeevan Ke Din",
    youtubeId: "vXlo3QOlIAg",
    note:
      "From Kishore Kumar's Bade Dil Wala (1983). One of those songs that quietly asks us to keep moving.",
  },
  {
    title: "Ek Pyaar Ka Nagma Hain",
    youtubeId: "92ibJ8Vey5w",
    note:
      "Lata Mangeshkar and Mukesh's tender duet from Shor (1972). This was the first song I ever learned to sing along with the guitar — and it has stayed with me ever since.",
  },
];

export const aiSongs = [
  {
    title: "Two Years Ago, On a Full Moon Night",
    youtubeId: "OWHXLycEBzY",
    explanation:
      "A reflective composition that blends the calm beauty of a full moon night with the emotional turbulence of human relationships. The song captures contrasts — serenity and intensity, loneliness and movement.",
    lyricist: "Papiya Ray",
    composition: "Rajib Sengupta (AI assisted)",
    aiTool: "Suno v4 for the music. Stock footage from pexels.com.",
  },
];

export const subjectOptions = ["Books", "Trek and Travel", "Music", "AI Media", "Blogs"];
